import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BookOpen, Bot, Check, Focus, HardDriveDownload, History, Info, ShoppingBasket, Trash2 } from 'lucide-react'
import { authApi, docsApi } from './lib/api'
import { emptyCanvas, mergeMigrated, migrateProjectsToCanvas } from './lib/canvas'
import { uid } from './lib/utils'
import AuthView from './components/AuthView'
import CanvasView from './components/CanvasView'
import { ShelfPanel, TrashPanel } from './components/BoxPanels'
import Roundtable from './components/Roundtable'
import HistoryModal from './components/HistoryModal'
import AISettingsModal, { normalizeSettings } from './components/AISettingsModal'
import LocalStoreModal from './components/LocalStoreModal'
import { LogoMark } from './components/Shared'

const STORAGE_KEY = 'pageisle:canvas'
const DOC_ID = 'canvas'

function loadLocalCanvas() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && Array.isArray(parsed.cards)) return parsed
    }
    const oldRaw = window.localStorage.getItem('pageisle:v1')
    if (oldRaw) {
      const parsedOld = JSON.parse(oldRaw)
      if (parsedOld && Array.isArray(parsedOld.projects) && parsedOld.projects.length) {
        return mergeMigrated(emptyCanvas(), migrateProjectsToCanvas(parsedOld.projects))
      }
    }
  } catch (error) { /* ignore */ }
  return emptyCanvas()
}

export default function App() {
  const [canvas, setCanvas] = useState(null)
  const [view, setView] = useState('canvas')
  const [auth, setAuth] = useState({ status: 'loading' })
  const [bootNotice, setBootNotice] = useState('')
  const [saveState, setSaveState] = useState('saved')
  const [toast, setToast] = useState(null)
  const [aiSettings, setAiSettings] = useState(null)
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false)
  const [localStoreOpen, setLocalStoreOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [roundtableSel, setRoundtableSel] = useState(null)
  const toastTimer = useRef(null)
  const canvasRef = useRef(null)
  const autoSaveCountRef = useRef(0)
  const [autoSaveCount, setAutoSaveCount] = useState(() => {
    try { return Number(window.localStorage.getItem('pageisle:autosave') || 0) || 0 } catch (error) { return 0 }
  })
  const [backupReminder, setBackupReminder] = useState(null)
  const AUTOSAVE_MS = typeof window !== 'undefined' && window.__PAGEISLE_AUTOSAVE_MS__ ? window.__PAGEISLE_AUTOSAVE_MS__ : 30000
  const BACKUP_REMINDER_MS = typeof window !== 'undefined' && window.__PAGEISLE_REMINDER_MS__ ? window.__PAGEISLE_REMINDER_MS__ : 1800000

  const notify = useCallback((message) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current)
    setToast({ message, id: Date.now() })
    toastTimer.current = window.setTimeout(() => setToast(null), 3400)
  }, [])

  useEffect(() => {
    let cancelled = false
    async function boot() {
      if (typeof window.fetch !== 'function') {
        setCanvas(loadLocalCanvas())
        setAuth({ status: 'local' })
        return
      }
      try {
        const me = await authApi.me()
        if (cancelled) return
        setAuth({ status: 'ok', email: me.email })
      } catch (error) {
        if (cancelled) return
        setBootNotice(error && error.message ? error.message : '')
        setAuth({ status: 'anon' })
      }
    }
    boot()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (auth.status !== 'ok') return
    let cancelled = false
    docsApi.list()
      .then((data) => {
        if (cancelled) return
        const doc = (data.docs || []).find((item) => item.id === DOC_ID)
        const projectsDoc = (data.docs || []).find((item) => (item.id === 'projects' || item.kind === 'projects') && item.payload && Array.isArray(item.payload.projects) && item.payload.projects.length)
        // 云端文档可能落后于本地（云端保存有防抖，刷新时最后一笔可能还没写回）：
        // 优先用云端内容，但展开/收起等视图状态以本地为准，云端缺失时整体回落到本地。
        const localCanvas = loadLocalCanvas()
        const hasLocal = localCanvas && Array.isArray(localCanvas.cards) && localCanvas.cards.length > 0
        let next = doc && doc.payload && Array.isArray(doc.payload.cards) ? doc.payload : (hasLocal ? localCanvas : emptyCanvas())
        if (hasLocal && localCanvas.view) {
          next = { ...next, view: localCanvas.view }
        }
        if (projectsDoc && !next.migratedFromProjects) {
          const migrated = migrateProjectsToCanvas(projectsDoc.payload.projects)
          next = mergeMigrated(next, migrated)
          docsApi.save(DOC_ID, 'canvas', next).catch(() => {})
          notify('已把旧书架的 ' + migrated.cards.length + ' 张卡片迁入画板。')
        }
        setCanvas(next)
        if (!doc) docsApi.save(DOC_ID, 'canvas', next).catch(() => {})
        const settingsDoc = (data.docs || []).find((item) => item.id === 'ai-settings')
        if (settingsDoc && settingsDoc.payload) setAiSettings(normalizeSettings(settingsDoc.payload))
      })
      .catch(() => { setSaveState('offline') })
    return () => { cancelled = true }
  }, [auth])

  useEffect(() => {
    canvasRef.current = canvas
  }, [canvas])

  useEffect(() => {
    autoSaveCountRef.current = autoSaveCount
  }, [autoSaveCount])

  // 卡片每 30 秒自动保存一次
  useEffect(() => {
    if (!canvas) return
    const timer = window.setInterval(() => {
      const current = canvasRef.current
      if (!current) return
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current)) } catch (error) { /* ignore */ }
      if (auth.status === 'ok') {
        docsApi.save(DOC_ID, 'canvas', current).then(() => setSaveState('saved')).catch(() => setSaveState('offline'))
      }
      setAutoSaveCount((count) => {
        const next = count + 1
        try { window.localStorage.setItem('pageisle:autosave', String(next)) } catch (error) { /* ignore */ }
        autoSaveCountRef.current = next
        return next
      })
    }, AUTOSAVE_MS)
    return () => window.clearInterval(timer)
  }, [canvas, auth, AUTOSAVE_MS])

  // 定期提醒用户保存整个网站的信息
  useEffect(() => {
    if (!canvas) return
    const timer = window.setInterval(() => {
      setBackupReminder({ id: Date.now(), count: autoSaveCountRef.current })
    }, BACKUP_REMINDER_MS)
    return () => window.clearInterval(timer)
  }, [canvas, BACKUP_REMINDER_MS])

  function handleBackupSaved() {
    setAutoSaveCount(0)
    autoSaveCountRef.current = 0
    try { window.localStorage.setItem('pageisle:autosave', '0') } catch (error) { /* ignore */ }
    setBackupReminder(null)
    notify('备份完成，自动保存记录已清空。')
  }

  useEffect(() => {
    if (!canvas) return
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(canvas)) } catch (error) { /* ignore */ }
    if (auth.status !== 'ok') { setSaveState(auth.status === 'local' ? 'local' : 'saved'); return }
    setSaveState('saving')
    const timer = window.setTimeout(async () => {
      try {
        await docsApi.save(DOC_ID, 'canvas', canvas)
        setSaveState('saved')
      } catch (error) { setSaveState('offline') }
    }, 500)
    return () => window.clearTimeout(timer)
  }, [canvas, auth])

  const patchCanvas = useCallback((updater) => {
    setCanvas((current) => {
      const base = current || emptyCanvas()
      const next = typeof updater === 'function' ? updater(base) : updater
      return { ...base, ...next }
    })
  }, [])

  function patchCard(cardId, patch) {
    setCanvas((current) => ({
      ...current,
      cards: current.cards.map((card) => {
        if (card.id !== cardId) return card
        const history = [{ at: new Date().toISOString(), title: card.title, content: card.content }, ...(card.history || [])].slice(0, 30)
        return { ...card, ...patch, history, updatedAt: new Date().toISOString() }
      }),
    }))
  }

  function handleCompile(title) {
    setCanvas((current) => {
      const collected = current.cards.filter((card) => card.status === 'collected').sort((a, b) => (a.order || 0) - (b.order || 0))
      const book = {
        id: uid('book'),
        title,
        cards: collected.map((card) => ({ id: card.id, title: card.title, content: card.content, kind: card.kind, color: card.color })),
        compiledAt: new Date().toISOString(),
      }
      return {
        ...current,
        books: [...(current.books || []), book],
        cards: current.cards.map((card) => card.status === 'collected' ? { ...card, status: 'inbook' } : card),
      }
    })
    setView('shelf')
    notify('《' + title + '》已成书，进入图书馆。')
  }

  function reorderCollected(fromIndex, toIndex) {
    setCanvas((current) => {
      const collectedIds = current.cards.filter((card) => card.status === 'collected').map((card) => card.id)
      if (toIndex < 0 || toIndex >= collectedIds.length || fromIndex === toIndex) return current
      const [moved] = collectedIds.splice(fromIndex, 1)
      collectedIds.splice(toIndex, 0, moved)
      const order = new Map(collectedIds.map((id, index) => [id, index]))
      return {
        ...current,
        cards: current.cards.map((card) => {
          if (card.status !== 'collected' || !order.has(card.id)) return card
          return { ...card, order: order.get(card.id) }
        }),
      }
    })
  }

  function handleSplitBook(bookId) {
    setCanvas((current) => {
      const book = (current.books || []).find((item) => item.id === bookId)
      if (!book) return current
      const restored = (book.cards || []).map((card, index) => ({
        ...card,
        x: 80 + index * 40,
        y: 80 + index * 50,
        status: 'active',
        column: 'inspiration',
        act: '未分幕',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }))
      return { ...current, books: current.books.filter((item) => item.id !== bookId), cards: [...current.cards, ...restored] }
    })
    setView('canvas')
    notify('书本已拆分回画板。')
  }

  function openRoundtableFor(cardIds) {
    if (!cardIds.length) { notify('请先勾选卡片。'); return }
    setRoundtableSel({ cardIds, openedAt: Date.now() })
  }

  const roundtableProject = useMemo(() => {
    if (!roundtableSel || !canvas) return null
    const ids = new Set(roundtableSel.cardIds)
    const cards = canvas.cards.filter((card) => ids.has(card.id))
    const connections = canvas.links.filter((link) => ids.has(link.source) && ids.has(link.target))
    return { id: 'canvas-rt', title: '画板圆桌', type: '随笔', cards, connections, updatedAt: '' }
  }, [roundtableSel, canvas])

  function saveRoundtableNote(cardId, note) {
    setCanvas((current) => ({
      ...current,
      cards: current.cards.map((card) => {
        if (card.id !== cardId) return card
        const previous = card.notes ? card.notes + '\n\n' : ''
        return { ...card, notes: previous + note, updatedAt: new Date().toISOString() }
      }),
    }))
    notify('建议已存为卡片批注。')
  }

  const handleAuthed = useCallback((email) => { setAuth({ status: 'ok', email }); notify('欢迎回到页屿。') }, [notify])

  const handleLogout = useCallback(async () => {
    try { await authApi.logout() } catch (error) { /* ignore */ }
    setAuth({ status: 'anon' })
    setView('canvas')
    notify('已退出登录。')
  }, [notify])

  const handleSaveAISettings = useCallback(async (payload) => {
    await docsApi.save('ai-settings', 'settings', payload)
    setAiSettings(normalizeSettings(payload))
    notify('AI 设置已保存，并同步到云端。')
  }, [notify])

  const handleLocalImport = useCallback((payload) => {
    if (payload && payload.canvas && Array.isArray(payload.canvas.cards)) setCanvas(payload.canvas)
    if (payload && payload.aiSettings) {
      setAiSettings(normalizeSettings(payload.aiSettings))
      docsApi.save('ai-settings', 'settings', payload.aiSettings).catch(() => {})
    }
    setView('canvas')
  }, [])

  const handleRestored = useCallback((payload) => {
    if (payload && Array.isArray(payload.cards)) setCanvas(payload)
    setView('canvas')
  }, [])

  if (auth.status === 'loading') {
    return <div className="app-loading"><span>正在打开页屿…</span></div>
  }

  if (auth.status === 'anon') {
    return (
      <>
        <AuthView onAuthed={handleAuthed} notice={bootNotice} />
        {toast ? <div className="toast" key={toast.id} role="status"><Info size={15} /><span>{toast.message}</span></div> : null}
      </>
    )
  }

  const SAVE_LABELS = { saving: '同步中', saved: '已同步', offline: '离线未同步', local: '本地模式' }
  const collectCount = canvas ? canvas.cards.filter((card) => card.status === 'collected').length : 0
  const trashCount = canvas ? canvas.cards.filter((card) => card.status === 'trash').length : 0
  const bookCount = canvas && canvas.books ? canvas.books.length : 0
  const shelfCount = collectCount + bookCount

  return (
    <>
      <div className="canvas-shell">
        <aside className="canvas-rail">
          <div className="rail__brand" title="页屿 Page Isle"><LogoMark size={36} /></div>
          <nav aria-label="主导航">
            <button className={'rail-link ' + (view === 'canvas' ? 'is-active' : '')} onClick={() => setView('canvas')}><Focus size={18} /><span>画板</span></button>
            <button className={'rail-link ' + (view === 'shelf' ? 'is-active' : '')} onClick={() => setView('shelf')}><ShoppingBasket size={18} /><span>收纳 · 图书馆{shelfCount ? <sup>{shelfCount}</sup> : null}</span></button>
            <button className={'rail-link ' + (view === 'trash' ? 'is-active' : '')} onClick={() => setView('trash')}><Trash2 size={18} /><span>回收箱{trashCount ? <sup>{trashCount}</sup> : null}</span></button>
          </nav>
          <div className="canvas-rail__bottom">
            <span className={'sync-chip sync-chip--' + saveState}>{SAVE_LABELS[saveState] || '已同步'}</span>
            <button className="rail-link rail-link--quiet" onClick={() => setAiSettingsOpen(true)}><Bot size={15} />AI 设置</button>
            <button className="rail-link rail-link--quiet" onClick={() => setLocalStoreOpen(true)}><HardDriveDownload size={15} />存储</button>
            <button className="rail-link rail-link--quiet" onClick={() => setHistoryOpen(true)}><History size={15} />历史</button>
            {auth.status === 'ok' ? <span className="canvas-rail__user" title={auth.email}>{auth.email}</span> : null}
            <span className="canvas-rail__build">build {typeof window !== 'undefined' && window.__PAGEISLE_BUILD__ ? window.__PAGEISLE_BUILD__ : '?'}</span>
            <button className="rail-link rail-link--quiet" onClick={handleLogout}>退出</button>
          </div>
        </aside>

        <main className="canvas-main">
          {view === 'canvas' && canvas ? (
            <CanvasView canvas={canvas} onChange={patchCanvas} onOpenRoundtable={openRoundtableFor} notify={notify} />
          ) : null}
          {view === 'shelf' && canvas ? (
            <ShelfPanel canvas={canvas} onCompile={handleCompile} onUncollect={(id) => patchCard(id, { status: 'active' })} onReorder={reorderCollected} onSplitBook={handleSplitBook} notify={notify} />
          ) : null}
          {view === 'trash' && canvas ? (
            <TrashPanel
              canvas={canvas}
              onRestore={(id) => patchCard(id, { status: 'active' })}
              onPurge={(id) => setCanvas((current) => ({ ...current, cards: current.cards.filter((card) => card.id !== id), links: current.links.filter((link) => link.source !== id && link.target !== id) }))}
            />
          ) : null}

        </main>
      </div>

      {historyOpen ? <HistoryModal docId={DOC_ID} onClose={() => setHistoryOpen(false)} onRestored={handleRestored} notify={notify} /> : null}
      {aiSettingsOpen ? (
        <AISettingsModal settings={aiSettings} onSave={handleSaveAISettings} onClose={() => setAiSettingsOpen(false)} notify={notify} />
      ) : null}
      {localStoreOpen ? (
        <LocalStoreModal canvas={canvas || emptyCanvas()} aiSettings={aiSettings} onImport={handleLocalImport} onBackupSaved={handleBackupSaved} onClose={() => setLocalStoreOpen(false)} notify={notify} />
      ) : null}
      {roundtableProject ? (
        <Roundtable
          key={roundtableSel.openedAt}
          project={roundtableProject}
          initialCardIds={roundtableSel.cardIds}
          onClose={() => setRoundtableSel(null)}
          onSaveNote={saveRoundtableNote}
          onOpenFinish={() => notify('画板模式下请在收纳箱中汇总成文。')}
          notify={notify}
          aiSettings={aiSettings}
        />
      ) : null}
      {backupReminder ? (
        <div className="backup-reminder" role="status" key={backupReminder.id}>
          <Info size={15} />
          <span>距上次完整备份，已自动保存 {backupReminder.count} 次。建议把整个网站的信息保存一份到本地。</span>
          <button className="btn btn--ink btn--small" onClick={() => { setBackupReminder(null); setLocalStoreOpen(true) }}>去备份</button>
          <button className="btn btn--ghost btn--small" onClick={() => setBackupReminder(null)}>稍后</button>
        </div>
      ) : null}
      {toast ? (
        <div className="toast" key={toast.id} role="status">
          {toast.message.includes('失败') ? <Info size={15} /> : <Check size={15} />}
          <span>{toast.message}</span>
        </div>
      ) : null}
    </>
  )
}
